import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWikiV2Client } from '../../../../hooks/useWikiV2';
import { invalidateWiki, useWikiQuery } from '../../shared/useWikiQueries';
import { wikiUi } from '../../shared/wikiUi';
import WikiIcon from '../../shared/WikiIcon';
import { WikiError, WikiLoading } from '../../shared/WikiStates';
import {
    BRAIN_OPS_TOASTS,
    barWidth,
    brainErrorToast,
    brainOpsKeys,
    brainOpsPaths,
    deniedToast,
    useBrainAccess,
} from '../review/brainOpsShared';
import '../review/brainOps.css';
import './brainSettings.css';

/**
 * AI Brain › Settings (E2 §3): three cards — Learning, Emotion reads,
 * Decision model. No Save button: every toggle applies instantly
 * (`PUT /wiki/brain/settings` with ONE key, contract §6.2) with an optimistic
 * flip that rolls back on failure. The fixed rows ("Always on", "Set by
 * NurtureCRM", "Admins only", "Set by plan") are display-only.
 *
 * Editors (readOnly) see the same cards; every control raises the prototype
 * denial toast instead of changing anything.
 *
 * Beyond the prototype (contract §6.1/§6.2): "Use learned and custom checks"
 * (`brain_mode`), the effective `emotion.enabled` with a confirm before the
 * switch-off purges stored per-message reads, and tier-forced values shown as
 * a locked "Set by your plan".
 */

const SETTING_PATHS = {
    propose_from_feedback: ['learning', 'propose_from_feedback'],
    read_feelings: ['emotion', 'read_feelings'],
    retention_days: ['emotion', 'retention_days'],
    shadow_enabled: ['shadow', 'enabled'],
};

/**
 * Apply one PUT key to a cached Settings payload (optimistic update).
 * `brain_mode` sets the effective `learning.mode` (+ `mode_source`/`paused`);
 * `read_feelings` also moves the effective `emotion.enabled`/`off_reason`.
 */
export function applySetting(settings, key, value) {
    if (!settings) return settings;
    if (key === 'brain_mode') {
        const baseOnly = value === 'base_only';
        return {
            ...settings,
            learning: {
                ...(settings.learning || {}),
                mode: value,
                mode_source: baseOnly ? 'setting' : null,
                paused: baseOnly,
            },
        };
    }
    if (key === 'read_feelings') {
        return {
            ...settings,
            emotion: {
                ...(settings.emotion || {}),
                read_feelings: value,
                enabled: Boolean(value),
                off_reason: value ? null : 'setting',
            },
        };
    }
    const path = SETTING_PATHS[key];
    if (!path) return settings;
    const [group, field] = path;
    return { ...settings, [group]: { ...(settings[group] || {}), [field]: value } };
}

/** Copy for the rows the prototype doesn't have (contract §6.1 `learning.mode`, emotion purge). */
export const SETTINGS_COPY = Object.freeze({
    modeTitle: 'Use learned and custom checks',
    modeDescription: 'When this is off, the Brain runs the NurtureCRM base tree only. Learned and custom checks are kept but paused, and approvals wait until it is back on.',
    modePlanNote: 'Your plan runs the base tree only, so learned and custom checks are paused.',
    planLocked: 'Set by your plan',
    purgeText: 'Turning this off deletes every stored per-message read now. Only the weekly topic totals stay. This can\'t be undone.',
    purgeConfirm: 'Turn off and delete reads',
    purgeCancel: 'Cancel',
    purgedToast: 'Emotion reads are off. Stored per-message reads were deleted.',
});

function Toggle({ on, label, onToggle, locked = false, testId }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={Boolean(on)}
            aria-label={label}
            aria-disabled={locked || undefined}
            className={`wk-brs-toggle${on ? ' is-on' : ''}`}
            onClick={onToggle}
            data-testid={testId}
        >
            <span className="wk-brs-knob" aria-hidden="true" />
        </button>
    );
}

function PlanLock({ testId = 'brain-settings-plan-lock' }) {
    return (
        <span className="wk-brs-locked" data-testid={testId}>
            <WikiIcon name="lock" size={12} />
            {SETTINGS_COPY.planLocked}
        </span>
    );
}

function Row({ title, description, extra, children, className = '', testId }) {
    return (
        <div className={`wk-brs-row ${className}`.trim()} data-testid={testId}>
            <div className="wk-brs-row-text">
                <div className="wk-brs-row-title">{title}</div>
                {description && <div className="wk-t-meta wk-brs-row-desc">{description}</div>}
                {extra}
            </div>
            {children}
        </div>
    );
}

function Card({ title, children, testId }) {
    return (
        <section className="wk-brs-card" aria-label={title} data-testid={testId}>
            <h3 className="wk-t-h wk-brs-card-title">{title}</h3>
            {children}
        </section>
    );
}

const JEV_STATUS = {
    connected: { tone: 'connected', label: 'Connected (transport only)' },
    off: { tone: 'off', label: 'Off' },
    unavailable: { tone: 'unavailable', label: 'Unavailable' },
};

export default function BrainSettingsTab(props) {
    const { canManage } = useBrainAccess(props);
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    const query = useWikiQuery(brainOpsKeys.settings(), brainOpsPaths.settings);
    const [saving, setSaving] = useState({});
    const [switching, setSwitching] = useState(false);
    const [confirmPurge, setConfirmPurge] = useState(false);

    const markSaving = useCallback((key, on) => {
        setSaving((prev) => {
            const next = { ...prev };
            if (on) next[key] = true; else delete next[key];
            return next;
        });
    }, []);

    const putSetting = useCallback(async (key, value) => {
        if (!canManage) {
            wikiUi.toast(BRAIN_OPS_TOASTS.settingsDenied);
            return false;
        }
        if (saving[key]) return false;
        const settingsKey = brainOpsKeys.settings();
        const previous = queryClient.getQueryData(settingsKey);
        queryClient.setQueryData(settingsKey, (old) => applySetting(old, key, value));
        markSaving(key, true);
        try {
            const res = await client.put(brainOpsPaths.settings, { [key]: value });
            if (res && typeof res === 'object') queryClient.setQueryData(settingsKey, res);
            // Mode / learning / emotion change what the tree, the health pill and the insights show.
            invalidateWiki(queryClient, ['brain', 'insights'], ['brain', 'tree'], ['brain', 'health']);
            return true;
        } catch (error) {
            queryClient.setQueryData(settingsKey, previous);
            wikiUi.toast(brainErrorToast(error, 'Changing settings'));
            return false;
        } finally {
            markSaving(key, false);
        }
    }, [canManage, client, markSaving, queryClient, saving]);

    const switchRouting = useCallback(async (target) => {
        if (!canManage) {
            wikiUi.toast(target === 'jev' ? BRAIN_OPS_TOASTS.switchJevDenied : deniedToast('Switching back to the router'));
            return;
        }
        if (switching) return;
        setSwitching(true);
        try {
            const res = await client.post(brainOpsPaths.routing, { target });
            if (res && typeof res === 'object') queryClient.setQueryData(brainOpsKeys.settings(), res);
            wikiUi.toast(target === 'jev' ? BRAIN_OPS_TOASTS.switchedToJev : BRAIN_OPS_TOASTS.switchedToRouter);
            invalidateWiki(queryClient, ['brain', 'tree'], ['brain', 'health']);
        } catch (error) {
            wikiUi.toast(brainErrorToast(error, target === 'jev' ? 'Switching to JEV' : 'Switching back to the router'));
        } finally {
            setSwitching(false);
        }
    }, [canManage, client, queryClient, switching]);

    const purgeEmotionReads = useCallback(async () => {
        setConfirmPurge(false);
        const ok = await putSetting('read_feelings', false);
        if (ok) wikiUi.toast(SETTINGS_COPY.purgedToast);
    }, [putSetting]);

    if (query.isLoading) {
        return <div className="wk-card" data-testid="brain-settings-tab"><WikiLoading label="Loading Brain settings…" /></div>;
    }
    if (query.isError) {
        return (
            <div className="wk-card" data-testid="brain-settings-tab">
                <WikiError error={query.error} onRetry={() => query.refetch()} />
            </div>
        );
    }

    const settings = query.data || {};
    const learning = settings.learning || {};
    const emotion = settings.emotion || {};
    const fixed = settings.fixed || {};
    const model = settings.decision_model || {};
    const jev = model.jev || {};
    const writer = model.writer || {};
    const usage = model.usage || {};
    const shadow = settings.shadow || {};

    const learnOn = learning.propose_from_feedback !== false;
    const learnPlanOff = learning.plan_allows === false;
    const modePlanForced = learning.mode_source === 'plan';
    const modeLearning = learning.mode !== 'base_only';
    const emoPlanOff = emotion.off_reason === 'plan' || emotion.plan_allows === false;
    const emoOn = (emotion.enabled ?? emotion.read_feelings) !== false;
    const shadowOn = shadow.enabled === true;
    const shadowPlanOff = shadow.plan_allows === false;
    const retention = Number(emotion.retention_days) || 30;
    const retentionOptions = Array.isArray(emotion.retention_options) && emotion.retention_options.length
        ? emotion.retention_options
        : [retention];
    const jevStatus = JEV_STATUS[jev.status] || JEV_STATUS.off;
    const jevLabel = jev.status === 'connected' ? 'Connected (transport only)' : (jev.label || jevStatus.label);
    const routedToJev = shadow.routing === 'jev';

    const denyUnlessManager = () => {
        if (canManage) return false;
        wikiUi.toast(BRAIN_OPS_TOASTS.settingsDenied);
        return true;
    };

    const toggleLearning = () => {
        if (denyUnlessManager()) return;
        putSetting('propose_from_feedback', !learnOn);
    };

    const toggleMode = () => {
        if (denyUnlessManager()) return;
        putSetting('brain_mode', modeLearning ? 'base_only' : 'learning');
    };

    const toggleEmotion = () => {
        if (denyUnlessManager()) return;
        // Turning reads off deletes the stored per-message reads at once (contract §6.1): confirm first.
        if (emoOn) { setConfirmPurge(true); return; }
        putSetting('read_feelings', true);
    };

    const toggleShadow = () => {
        if (denyUnlessManager()) return;
        putSetting('shadow_enabled', !shadowOn);
    };

    const planNote = (off, message, testId = 'brain-settings-plan-note') => (off && message
        ? <div className="wk-brs-plan" data-testid={testId}>{message}</div>
        : null);

    return (
        <div className="wk-brs wk-stack" data-testid="brain-settings-tab">
            <Card title="Learning" testId="brain-settings-learning">
                <Row
                    title="Propose changes from feedback"
                    description="Thumbs up and down, corrections and page-owner answers become memories. Repeats become proposals."
                    extra={planNote(learnPlanOff, learning.plan_message)}
                >
                    {learnPlanOff ? <PlanLock testId="brain-learning-plan-lock" /> : (
                        <Toggle
                            on={learnOn}
                            label="Propose changes from feedback"
                            locked={!canManage}
                            onToggle={toggleLearning}
                            testId="brain-toggle-learning"
                        />
                    )}
                </Row>
                <Row
                    title={SETTINGS_COPY.modeTitle}
                    description={SETTINGS_COPY.modeDescription}
                    extra={modePlanForced
                        ? <div className="wk-brs-plan" data-testid="brain-mode-plan-note">{SETTINGS_COPY.modePlanNote}</div>
                        : null}
                    testId="brain-settings-mode"
                >
                    {modePlanForced ? <PlanLock testId="brain-mode-plan-lock" /> : (
                        <Toggle
                            on={modeLearning}
                            label={SETTINGS_COPY.modeTitle}
                            locked={!canManage}
                            onToggle={toggleMode}
                            testId="brain-toggle-mode"
                        />
                    )}
                </Row>
                <Row
                    title="Admin approval for every change"
                    description="Required on every plan. The Brain never switches on its own changes."
                >
                    <span className="wk-brs-locked" data-testid="brain-settings-always-on">
                        <WikiIcon name="lock" size={12} />
                        Always on
                    </span>
                </Row>
                <Row
                    title="When to propose"
                    description={`A guardrail after the same mistake ${fixed.guardrail_after ?? 2} times. A new check after the same unsure spot ${fixed.check_after ?? 3} times.`}
                >
                    <span className="wk-brs-fixed" data-testid="brain-settings-fixed">Set by NurtureCRM</span>
                </Row>
                <Row
                    title="Replay test"
                    description={`Catch at least ${fixed.replay_own_min ?? 80}% of its own cases, and fire on no more than ${fixed.replay_control_max ?? 20}% of unrelated questions.`}
                >
                    <span className="wk-brs-fixed" data-testid="brain-settings-fixed">Set by NurtureCRM</span>
                </Row>
            </Card>

            <Card title="Emotion reads" testId="brain-settings-emotion">
                <Row
                    title="Read how people feel"
                    description="Adjusts length, care and warmth. It never changes the facts in an answer."
                    extra={planNote(emoPlanOff, emotion.plan_message)}
                >
                    {emoPlanOff ? <PlanLock testId="brain-emotion-plan-lock" /> : (
                        <Toggle
                            on={emoOn}
                            label="Read how people feel"
                            locked={!canManage}
                            onToggle={toggleEmotion}
                            testId="brain-toggle-emotion"
                        />
                    )}
                </Row>
                {confirmPurge && (
                    <div className="wk-brs-confirm" role="alertdialog" aria-label="Turn off emotion reads?" data-testid="brain-emotion-purge-confirm">
                        <WikiIcon name="alert-triangle" size={15} className="wk-brs-confirm-icon" />
                        <span className="wk-brs-confirm-text">{SETTINGS_COPY.purgeText}</span>
                        <span className="wk-brs-confirm-actions">
                            <button
                                type="button"
                                className="wk-btn wk-btn--secondary wk-brs-confirm-btn"
                                onClick={() => setConfirmPurge(false)}
                                data-testid="brain-emotion-purge-cancel"
                            >
                                {SETTINGS_COPY.purgeCancel}
                            </button>
                            <button
                                type="button"
                                className="wk-btn wk-btn--danger wk-brs-confirm-btn"
                                onClick={purgeEmotionReads}
                                disabled={Boolean(saving.read_feelings)}
                                data-testid="brain-emotion-purge-confirm-btn"
                            >
                                {SETTINGS_COPY.purgeConfirm}
                            </button>
                        </span>
                    </div>
                )}
                <Row
                    title="Keep per-message reads for"
                    description="After this, only the weekly topic totals remain."
                >
                    {canManage && !emoPlanOff ? (
                        <span className="wk-brs-select-wrap">
                            <select
                                className="wk-brs-select"
                                value={retention}
                                onChange={(event) => putSetting('retention_days', Number(event.target.value))}
                                disabled={Boolean(saving.retention_days)}
                                aria-label="Keep per-message reads for"
                                data-testid="brain-retention-select"
                            >
                                {retentionOptions.map((days) => (
                                    <option key={days} value={days}>{days} {Number(days) === 1 ? 'day' : 'days'}</option>
                                ))}
                            </select>
                            <WikiIcon name="chevron-down" size={13} className="wk-brs-select-chevron" />
                        </span>
                    ) : (
                        <button
                            type="button"
                            className="wk-brs-select wk-brs-select--static"
                            aria-disabled="true"
                            onClick={() => wikiUi.toast(canManage ? (emotion.plan_message || BRAIN_OPS_TOASTS.settingsDenied) : BRAIN_OPS_TOASTS.settingsDenied)}
                            data-testid="brain-retention-static"
                        >
                            {retention} {retention === 1 ? 'day' : 'days'}
                            <WikiIcon name="chevron-down" size={13} className="wk-brs-select-chevron-inline" />
                        </button>
                    )}
                </Row>
                <Row
                    title="Who can investigate conversations"
                    description="Every opened conversation and every name reveal is written to the audit log."
                >
                    <span className="wk-brs-strong" data-testid="brain-settings-investigators">{fixed.investigators || 'Admins only'}</span>
                </Row>
            </Card>

            <Card title="Decision model" testId="brain-settings-model">
                <Row
                    title="JEV by TypeSafe AI"
                    description={`Version ${jev.version || 'jev-1.13.0'}, pinned and managed by NurtureCRM. Calls go through the NurtureCRM gateway; your workspace never holds the key. Connection status only confirms transport availability; the Ask trace confirms whether JEV ran.`}
                >
                    <span className={`wk-brs-status wk-brs-status--${jevStatus.tone}`} data-testid="brain-jev-status">
                        <span className="wk-brs-dot" aria-hidden="true" />
                        {jevLabel}
                    </span>
                </Row>
                <Row
                    title={writer.label || `Writer: ${writer.model || 'gpt-4o'} through the AI gateway`}
                    description="Writes every answer in clear language from the passages JEV chose, following JEV's brief. It never decides which sources to trust."
                >
                    <span className="wk-brs-fixed">Set by plan</span>
                </Row>
                <div className="wk-brs-row wk-brs-usage" data-testid="brain-settings-usage">
                    <div className="wk-brs-usage-head">
                        <span className="wk-brs-row-title wk-brs-usage-title">This month&apos;s checks</span>
                        <span className="wk-brs-usage-label">{usage.label || ''}</span>
                    </div>
                    <div className="wk-brs-usage-track">
                        <div className="wk-brs-usage-fill" style={{ width: `${barWidth(usage.pct)}%` }} data-testid="brain-settings-usage-fill" />
                    </div>
                </div>
                <Row
                    title="Shadow test: category routing"
                    description={shadow.summary || ''}
                    className="wk-brs-row--wrap"
                    testId="brain-settings-shadow"
                >
                    {shadowPlanOff ? <PlanLock testId="brain-shadow-plan-lock" /> : (
                        <span className="wk-brs-shadow-control">
                            <span className="wk-brs-shadow-label">
                                Run shadow comparisons
                                <span className="wk-brs-shadow-note">Admin-only · requires an enabled tenant JEV policy and non-zero caps.</span>
                            </span>
                            <Toggle
                                on={shadowOn}
                                label="Run shadow comparisons (admin-only)"
                                locked={!canManage}
                                onToggle={toggleShadow}
                                testId="brain-toggle-shadow"
                            />
                        </span>
                    )}
                    <button
                        type="button"
                        className="wk-btn wk-btn--primary wk-brs-switch"
                        onClick={() => switchRouting(routedToJev ? 'router' : 'jev')}
                        disabled={switching}
                        aria-busy={switching || undefined}
                        data-testid="brain-switch-routing"
                    >
                        {routedToJev ? 'Switch back to the router' : 'Switch to JEV'}
                    </button>
                </Row>
            </Card>
        </div>
    );
}
